import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import TranscriptEditor from './TranscriptEditor';
const API_URL = (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');

const authHeaders = (token) => {
  const h = { 'Content-Type': 'application/json' };
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
};


const ROLE_LABELS = {
  creator: 'Creator',
  reviewer: 'Reviewer',
  final_approver: 'Final Approver',
  distributor: 'Distributor',
  manager: 'Manager',
};

const ROLE_COLORS = {
  creator: '#6366f1',
  reviewer: '#f59e0b',
  final_approver: '#10b981',
  manager: '#3b82f6',
  distributor: '#94a3b8',
};

const STATUS_LABELS = {
  draft:              { label: 'Draft',              color: '#94a3b8' },
  in_review:          { label: 'In Review',          color: '#f59e0b' },
  changes_requested:  { label: 'Changes Requested',  color: '#ef4444' },
  final_review:       { label: 'Final Review',       color: '#8b5cf6' },
  approved:           { label: 'Approved',           color: '#10b981' },
  deployed:           { label: 'Deployed',           color: '#3b82f6' },
  content_approved:   { label: 'Content Approved', color: '#8b5cf6' },
  transcripts_pending:{ label: 'Transcripts',      color: '#f59e0b' },
  audio_pending:      { label: 'Audio Generating',  color: '#3b82f6' },
  audio_ready:        { label: 'Audio Ready',        color: '#10b981' },
  no_audio:           { label: 'No Audio Needed',    color: '#06b6d4' },
};

const formatEmail = (email) => {
  if (!email) return 'Unknown';
  return email.split('@')[0];
};

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

export default function ReviewSidebar({ templateId, currentPageId, token, role, getAccessTokenSilently = null }) {
  const [collapsed, setCollapsed] = useState(false);
  const [review, setReview] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [showResolved, setShowResolved] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('open'); // 'open' | 'resolved'
  const pollRef = useRef(null);
  const [pageFilter, setPageFilter] = useState('all');
  const [team, setTeam] = useState({});
  const [showTranscriptEditor, setShowTranscriptEditor] = useState(false);

  // pageFilter intentionally stays 'all' by default so all comments are visible.
  // currentPageId is used directly for the add-comment label and page_id field (line ~118, ~437).

  const fetchAll = useCallback(async () => {
    if (!templateId || !token) return;
    try {
      const [statusRes, commentsRes, teamRes] = await Promise.all([
        fetch(`${API_URL}/lesson/templates/${templateId}/review/status`, { headers: authHeaders(token) }),
        fetch(`${API_URL}/lesson/templates/${templateId}/review/comments`, { headers: authHeaders(token) }),
        fetch(`${API_URL}/lesson/templates/${templateId}/assigned-team`, { headers: authHeaders(token) }),
      ]);
      const statusData = await statusRes.json();
      const commentsData = await commentsRes.json();
      const teamData = await teamRes.json();
      if (statusData.success) setReview(statusData.review);
      if (commentsData.success) setComments(commentsData.comments);
      if (teamData.success) setTeam(teamData.team);
    } catch (e) {
      console.warn('[ReviewSidebar] fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, [templateId, token]);

  useEffect(() => {
    fetchAll();
    pollRef.current = setInterval(fetchAll, 8000);
    return () => clearInterval(pollRef.current);
  }, [fetchAll]);


  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setAddingComment(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/lesson/templates/${templateId}/review/comments`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({
          text: newComment.trim(),
          page_id: currentPageId || 'general',
        }),
      });
      const data = await res.json();
      if (data.success) {
        setComments(prev => [...prev, data.comment]);
        setNewComment('');
      } else {
        setError(data.error || 'Failed to add comment');
      }
    } catch { setError('Failed to add comment'); }
    finally { setAddingComment(false); }
  };

  const handleReply = async (commentId) => {
    if (!replyText.trim()) return;
    setError('');
    try {
      const res = await fetch(
        `${API_URL}/lesson/templates/${templateId}/review/comments/${commentId}/reply`,
        {
          method: 'POST',
          headers: authHeaders(token),
          body: JSON.stringify({ text: replyText.trim() }),
        }
      );
      const data = await res.json();
      if (data.success) {
        setComments(prev => prev.map(c =>
          c.comment_id === commentId
            ? { ...c, replies: [...c.replies, data.reply] }
            : c
        ));
        setReplyingTo(null);
        setReplyText('');
      } else { setError(data.error || 'Failed to reply'); }
    } catch { setError('Failed to reply'); }
  };

  const handleResolve = async (commentId) => {
    setError('');
    try {
      const res = await fetch(
        `${API_URL}/lesson/templates/${templateId}/review/comments/${commentId}/resolve`,
        { method: 'POST', headers: authHeaders(token) }
      );
      const data = await res.json();
      if (data.success) {
        setComments(prev => prev.map(c =>
          c.comment_id === commentId ? { ...c, resolved: true } : c
        ));
      } else { setError(data.error || 'Failed to resolve'); }
    } catch { setError('Failed to resolve'); }
  };

  const handleOverride = async (commentId) => {
    const note = window.prompt('Override reason (optional):');
    if (note === null) return;
    setError('');
    try {
      const res = await fetch(
        `${API_URL}/lesson/templates/${templateId}/review/comments/${commentId}/resolve`,
        {
          method: 'POST',
          headers: authHeaders(token),
          body: JSON.stringify({ override: true, override_note: note }),
        }
      );
      const data = await res.json();
      if (data.success) {
        setComments(prev => prev.map(c =>
          c.comment_id === commentId
            ? { ...c, resolved: true, overridden: true, override_note: note }
            : c
        ));
      } else { setError(data.error || 'Failed to override'); }
    } catch { setError('Failed to override'); }
  };

  const handleWorkflowAction = async (endpoint, note = '') => {
    setActionLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${API_URL}/lesson/templates/${templateId}/review/${endpoint}`,
        {
          method: 'POST',
          headers: authHeaders(token),
          body: JSON.stringify({ note }),
        }
      );
      const data = await res.json();
      if (data.success) {
        await fetchAll();
      } else { setError(data.error || 'Action failed'); }
    } catch { setError('Action failed'); }
    finally { setActionLoading(false); }
  };

  const openComments = comments.filter(c => !c.resolved);
  const resolvedComments = comments.filter(c => c.resolved);
  const tabFiltered = activeTab === 'open' ? openComments : resolvedComments;
  const visibleComments = pageFilter === 'all'
    ? tabFiltered
    : tabFiltered.filter(c => (c.page_id || 'general') === pageFilter);

  // Group by page
  const grouped = visibleComments.reduce((acc, c) => {
    const key = c.page_id || 'general';
    if (!acc[key]) acc[key] = [];
    acc[key].push(c);
    return acc;
  }, {});

  const statusInfo = STATUS_LABELS[review?.status] || STATUS_LABELS.draft;
  console.log('[ReviewSidebar] role:', role, 'status:', review?.status);
  // FIND the entire collapsed block and REPLACE:

  if (collapsed) {
  return (
    <div style={{ width: '0px', minWidth: '0px', position: 'relative' }}>
      <button
        onClick={() => setCollapsed(false)}
        title="Open review panel"
        style={{
          position: 'fixed',
          right: '0',           // ← was 'left: 0', now right edge
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'rgba(0,255,157,0.12)',
          border: '1px solid rgba(0,255,157,0.3)',
          borderRight: 'none',             // ← flat on the right wall
          borderRadius: '8px 0 0 8px',     // ← rounded on left side only
          width: '28px',
          padding: '12px 4px',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '6px',
          boxShadow: '-2px 0 12px rgba(0,255,157,0.15)',
          zIndex: 999,
          backdropFilter: 'blur(8px)',
        }}
      >
        {openComments.length > 0 && (
          <span style={{
            background: '#ef4444',
            color: '#fff',
            borderRadius: '50%',
            width: '16px',
            height: '16px',
            fontSize: '9px',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {openComments.length}
          </span>
        )}
        <span style={{ fontSize: '12px' }}>💬</span>
        <span style={{
          writingMode: 'vertical-rl',
          fontSize: '9px',
          fontWeight: 700,
          color: '#00ff9d',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}>
          Review
        </span>
      </button>
    </div>
  );
}




  // ── Expanded sidebar ───────────────────────────────────────────────────────
  return (
    <>
    <div style={{
      width: '300px',
      minWidth: '300px',
      height: '100%',
      overflowY: 'auto',
      background: 'rgba(2,6,23,0.92)',
      borderLeft: '1px solid rgba(0,255,157,0.12)',
      backdropFilter: 'blur(12px)',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'inherit',
      fontSize: '13px',
    }}>

      {/* ── Header ── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        borderBottom: '1px solid rgba(0,255,157,0.1)',
        background: 'rgba(15,23,42,0.8)',
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#f8fafc' }}>
            Review
          </h3>
          <span style={{
            fontSize: '11px', fontWeight: 600, color: '#fff',
            padding: '2px 8px', borderRadius: '20px',
            background: statusInfo.color,
          }}>
            {statusInfo.label}
          </span>
        </div>
        <button
          onClick={() => setCollapsed(true)}
          style={{
            background: 'rgba(0,255,157,0.08)', border: '1px solid rgba(0,255,157,0.2)',
            cursor: 'pointer', color: '#00ff9d', fontSize: '13px', padding: '4px 8px',
            borderRadius: '6px', lineHeight: 1, fontWeight: 600,
          }}
          title="Collapse review panel"
        >
          ‹ Hide
        </button>
      </div>

      {error && (
        <div style={{
          background: 'rgba(239,68,68,0.12)', color: '#f87171',
          padding: '8px 16px', fontSize: '12px',
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '24px', color: '#94a3b8', textAlign: 'center' }}>
          Loading...
        </div>
      ) : (
        <>
        {/* ── Draft lock for reviewer/final_approver ── */}
          {['reviewer', 'final_approver'].includes(role) && review?.status === 'draft' && (
            <div style={{
              margin: '24px 16px', background: 'rgba(15,23,42,0.6)',
              border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px',
              padding: '20px', textAlign: 'center',
            }}>
              <div style={{ fontSize: '28px', marginBottom: '10px' }}>🔒</div>
              <div style={{ color: '#f8fafc', fontWeight: 700, fontSize: '13px', marginBottom: '6px' }}>
                Not yet submitted
              </div>
              <div style={{ color: '#64748b', fontSize: '12px', lineHeight: 1.5 }}>
                The creator hasn't submitted this lesson for review yet.
              </div>
            </div>
          )}
          {/* ── Team info ── */}
          {Object.keys(team).length > 0 && (
            <div style={{
              padding: '10px 16px',
              borderBottom: '1px solid rgba(0,255,157,0.08)',
              background: 'rgba(15,23,42,0.4)',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            }}>
              {Object.entries(team).map(([teamRole, members]) =>
                members.map(member => (
                  <div key={member.user_id} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px' }}>
                    <span style={{
                      fontWeight: 700,
                      color: ROLE_COLORS[teamRole] || '#6366f1',
                      textTransform: 'capitalize',
                      minWidth: '90px',
                    }}>
                      {teamRole.replace('_', ' ')}:
                    </span>
                    <span style={{ color: '#94a3b8' }}>
                      {member.name || member.email?.split('@')[0]}
                    </span>
                  </div>
                ))
              )}
            </div>
          )}

          {/* ── REVIEWER view: action buttons at top, then add comment ── */}
          {role === 'reviewer' && (
            <>
              {review?.status === 'in_review' && (
                <div style={{ padding: '12px 16px', background: 'rgba(15,23,42,0.6)', borderBottom: '1px solid rgba(0,255,157,0.08)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <button
                    style={actionBtn('#ef4444')}
                    disabled={actionLoading}
                    onClick={() => handleWorkflowAction('request-changes')}
                  >
                    {actionLoading ? '...' : '🔄 Request Changes'}
                  </button>
                  <button
                    style={actionBtn('#8b5cf6')}
                    disabled={actionLoading}
                    onClick={() => handleWorkflowAction('escalate', 'Conflicting feedback')}
                  >
                    {actionLoading ? '...' : '⚠️ Escalate Conflict'}
                  </button>
                </div>
              )}
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: 600, color: '#00ff9d', textTransform: 'uppercase', opacity: 0.7 }}>
                  Add Comment · {currentPageId || 'General'}
                </p>
                <textarea
                  value={newComment}
                  onChange={e => setNewComment(e.target.value)}
                  placeholder="Describe what needs to change..."
                  style={textareaStyle}
                  rows={3}
                />
                <button
                  style={actionBtn(newComment.trim() ? '#6366f1' : '#cbd5e1')}
                  disabled={addingComment || !newComment.trim()}
                  onClick={handleAddComment}
                >
                  {addingComment ? 'Adding...' : '💬 Add Comment'}
                </button>
              </div>
            </>
          )}

          {/* ── FINAL APPROVER view ── */}
          {/* ── FINAL APPROVER view ── */}
          {/* ── FINAL APPROVER: content approve (first approval) ── */}
          {role === 'final_approver' && ['in_review', 'final_review'].includes(review?.status) && (
            <div style={{ padding: '12px 16px', background: 'rgba(15,23,42,0.6)', borderBottom: '1px solid rgba(0,255,157,0.08)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {review?.status === 'final_review' && (() => {
                const escalation = review?.history?.slice().reverse().find(h => h.action === 'conflict_escalated');
                if (!escalation) return null;
                return (
                  <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '8px', padding: '10px 12px', marginBottom: '4px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#fbbf24', marginBottom: '4px' }}>⚠️ Escalated by Reviewer</div>
                    <div style={{ fontSize: '12px', color: '#fcd34d' }}>{escalation.note || 'No reason provided'}</div>
                  </div>
                );
              })()}
              <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>
                Step 1 of 2: Approve content. Creator will then generate audio.
              </div>
              <button
                style={actionBtn('#8b5cf6')}
                disabled={actionLoading}
                onClick={() => handleWorkflowAction('content-approve')}
              >
                {actionLoading ? '...' : '✅ Content Approve'}
              </button>
              <button
                style={actionBtn('#ef4444')}
                disabled={actionLoading}
                onClick={() => handleWorkflowAction('request-changes')}
              >
                {actionLoading ? '...' : '🔄 Request Changes'}
              </button>
            </div>
          )}

          {/* ── FINAL APPROVER: final approve after audio ready ── */}
          {role === 'final_approver' && review?.status === 'audio_ready' && (
            <div style={{ padding: '12px 16px', background: 'rgba(15,23,42,0.6)', borderBottom: '1px solid rgba(0,255,157,0.08)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: '8px', padding: '10px 12px', marginBottom: '4px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#34d399' }}>🎙️ Audio is ready</div>
                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>Listen to the lesson, then give final approval.</div>
              </div>
              <button
                style={actionBtn('#10b981')}
                disabled={actionLoading}
                onClick={() => handleWorkflowAction('final-approve')}
              >
                {actionLoading ? '...' : '🏁 Final Approve'}
              </button>
              <button
                style={actionBtn('#ef4444')}
                disabled={actionLoading}
                onClick={() => handleWorkflowAction('request-changes')}
              >
                {actionLoading ? '...' : '🔄 Request Changes'}
              </button>

              <button
                style={actionBtn('#f59e0b')}
                disabled={actionLoading}
                onClick={async () => {
                  const note = window.prompt('Reason for rejecting audio (optional):');
                  if (note === null) return;
                  await handleWorkflowAction('reject-audio', note);
                }}
              >
                {actionLoading ? '...' : '🔁 Reject Audio — Ask Creator to Redo'}
              </button>
            </div>
          )}


          {/* ── FINAL APPROVER: final approve when audio was skipped ── */}
{role === 'final_approver' && review?.status === 'no_audio' && (
  <div style={{ padding: '12px 16px', background: 'rgba(15,23,42,0.6)', borderBottom: '1px solid rgba(0,255,157,0.08)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
    <div style={{ background: 'rgba(6,182,212,0.1)', border: '1px solid rgba(6,182,212,0.2)', borderRadius: '8px', padding: '10px 12px', marginBottom: '4px' }}>
      <div style={{ fontSize: '11px', fontWeight: 700, color: '#22d3ee' }}>⏭️ No audio — lesson ready for final approval</div>
      <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>The creator skipped audio generation. Review the lesson content and approve.</div>
    </div>
    <button
      style={actionBtn('#10b981')}
      disabled={actionLoading}
      onClick={() => handleWorkflowAction('final-approve')}
    >
      {actionLoading ? '...' : '🏁 Final Approve'}
    </button>
    <button
      style={actionBtn('#ef4444')}
      disabled={actionLoading}
      onClick={() => handleWorkflowAction('request-changes')}
    >
      {actionLoading ? '...' : '🔄 Request Changes'}
    </button>
  </div>
)}

          {/* ── CREATOR: content approved — generate transcripts/audio ── */}
          {role === 'creator' && ['content_approved', 'transcripts_pending', 'audio_pending', 'audio_ready', 'no_audio'].includes(review?.status) && (
            <div style={{ margin: '12px 16px', background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.3)', borderRadius: '10px', padding: '14px' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#a78bfa', marginBottom: '6px' }}>
                🎙️ Content approved! Add read-aloud audio.
              </div>
              <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '10px', lineHeight: 1.5 }}>
                {review?.status === 'audio_ready'
  ? 'Audio is generated and ready. Waiting for final approval.'
  : review?.status === 'no_audio'
  ? 'You skipped audio. The lesson has been sent to the final approver for approval without audio.'
  : review?.status === 'audio_pending'
  ? 'Audio is being generated...'
  : 'Review and edit the auto-generated transcripts, select a voice, then generate audio.'}
              </div>
              {review?.history?.slice().reverse().find(h => h.action === 'audio_rejected') && (
                <div style={{ fontSize: '11px', color: '#f87171', marginBottom: '8px', background: 'rgba(239,68,68,0.08)', padding: '8px', borderRadius: '6px' }}>
                  ⚠️ Audio rejected: {review.history.slice().reverse().find(h => h.action === 'audio_rejected')?.note || 'Please regenerate audio.'}
                </div>
              )}
              {['content_approved', 'transcripts_pending'].includes(review?.status) && (
  <button
    style={actionBtn('#8b5cf6')}
    onClick={() => setShowTranscriptEditor(true)}
  >
    ✏️ Edit Transcripts & Generate Audio
  </button>
)}
{review?.status === 'no_audio' && (
  <div style={{ fontSize: '11px', color: '#06b6d4', padding: '6px 8px', background: 'rgba(6,182,212,0.08)', borderRadius: '6px', border: '1px solid rgba(6,182,212,0.2)' }}>
    ⏭️ Audio skipped — awaiting final approval
  </div>
)}
            </div>
          )}

          {/* ── APPROVED banner ── */}
          {review?.status === 'approved' && (
            <div style={{
              margin: '12px 16px', background: 'rgba(16,185,129,0.1)', color: '#34d399',border: '1px solid rgba(16,185,129,0.2)',
              padding: '10px 12px', borderRadius: '8px',
              fontWeight: 600, fontSize: '12px', textAlign: 'center',
            }}>
              ✅ Lesson approved
            </div>
          )}

          {/* ── Comments tabs ── */}
          <div style={{
            display: 'flex',
            borderBottom: '1px solid rgba(0,255,157,0.08)',
            background: 'rgba(15,23,42,0.6)',
          }}>
            {['open', 'resolved'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  flex: 1, padding: '8px', border: 'none', background: 'none',
                  fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                  color: activeTab === tab ? '#00ff9d' : '#475569',
                  borderBottom: activeTab === tab ? '2px solid #00ff9d' : '2px solid transparent',
                }}
              >
                {tab === 'open' ? `Open (${openComments.length})` : `Resolved (${resolvedComments.length})`}
              </button>
            ))}
          </div>

          {/* ── Page filter pills ── */}
          {(() => {
            const pages = [...new Set(comments.map(c => c.page_id || 'general'))];
            if (pages.length <= 1) return null;
            return (
              <div style={{
                padding: '8px 16px',
                borderBottom: '1px solid #e2e8f0',
                background: '#fff',
                display: 'flex',
                gap: '6px',
                flexWrap: 'wrap',
              }}>
                <button
                  onClick={() => setPageFilter('all')}
                  style={{
                    padding: '3px 10px', borderRadius: '20px', fontSize: '11px',
                    fontWeight: 600, border: '1.5px solid',
                    background: pageFilter === 'all' ? 'rgba(0,255,157,0.1)' : 'transparent',
                    color: pageFilter === 'all' ? '#00ff9d' : '#475569',
                    borderColor: pageFilter === 'all' ? 'rgba(0,255,157,0.4)' : 'rgba(255,255,255,0.08)',
                    cursor: 'pointer',
                  }}
                >
                  All
                </button>
                {pages.map(p => (
                  <button
                    key={p}
                    onClick={() => setPageFilter(p)}
                    style={{
                      padding: '3px 10px', borderRadius: '20px', fontSize: '11px',
                      fontWeight: 600, border: '1.5px solid',
                      background: pageFilter === p ? 'rgba(0,255,157,0.1)' : 'transparent',
                      color: pageFilter === p ? '#00ff9d' : '#475569',
                      borderColor: pageFilter === p ? 'rgba(0,255,157,0.4)' : 'rgba(255,255,255,0.08)',
                      cursor: 'pointer',
                      maxWidth: '80px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {p}
                  </button>
                ))}
              </div>
            );
          })()}
          

          {/* ── Comments list ── */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px 24px' }}>
            {Object.keys(grouped).length === 0 && (
              <div style={{ color: '#94a3b8', fontSize: '12px', textAlign: 'center', padding: '24px 0' }}>
                {activeTab === 'open' ? 'No open comments.' : 'No resolved comments.'}
              </div>
            )}
            {Object.entries(grouped).map(([pageId, pageComments]) => (
              <div key={pageId} style={{ marginBottom: '16px' }}>
                {/* Page label */}
                <div style={{
                  fontSize: '10px', fontWeight: 700, color: '#94a3b8',
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                  marginBottom: '6px', marginTop: '8px',
                }}>
                  📄 {pageId}
                </div>

                {pageComments.map(comment => (
                  <div
                    key={comment.comment_id}
                    style={{
                      background: 'rgba(15,23,42,0.6)',
                      borderRadius: '8px',
                      padding: '10px 12px',
                      marginBottom: '8px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                      borderLeft: `3px solid ${ROLE_COLORS[comment.author_role] || '#6366f1'}`,
                      opacity: comment.resolved ? 0.6 : 1,
                    }}
                  >
                    {/* Who + when */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                      <div style={{
                        width: '24px', height: '24px', borderRadius: '50%',
                        background: ROLE_COLORS[comment.author_role] || '#6366f1',
                        color: '#fff', fontSize: '10px', fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        {formatEmail(comment.author_email).charAt(0).toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#f8fafc' }}>
                          {formatEmail(comment.author_email)}
                        </div>
                        <div style={{ fontSize: '10px', color: '#94a3b8' }}>
                          {ROLE_LABELS[comment.author_role] || comment.author_role} · {timeAgo(comment.created_at)}
                        </div>
                      </div>
                      {comment.resolved && !comment.overridden && (
                      <span style={{ fontSize: '10px', color: '#10b981', fontWeight: 600 }}>✓</span>
                    )}
                    {comment.overridden && (
                      <span style={{ fontSize: '10px', color: '#f59e0b', fontWeight: 600 }}>↺ overridden</span>
                    )}
                    </div>

                    {/* Comment text */}
                    <p style={{ margin: '0 0 8px', color: '#94a3b8', lineHeight: 1.5, fontSize: '13px' }}>
                      {comment.text}
                    </p>

                    {/* Replies */}
                    {comment.replies.length > 0 && (
                      <div style={{ marginBottom: '6px' }}>
                        {comment.replies.map(reply => (
                          <div key={reply.reply_id} style={{
                            background: 'rgba(255,255,255,0.04)', borderRadius: '6px',
                            padding: '6px 8px', marginBottom: '4px',
                            borderLeft: `2px solid ${ROLE_COLORS[reply.author_role] || '#94a3b8'}`,
                          }}>
                            <div style={{ fontSize: '10px', fontWeight: 600, color: '#475569', marginBottom: '2px' }}>
                              {formatEmail(reply.author_email)} · {ROLE_LABELS[reply.author_role]}
                            </div>
                            <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>{reply.text}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Actions */}
                    {!comment.resolved && (
                      <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                        {role !== 'distributor' && (
                          <button
                            style={smallBtn('#6366f1')}
                            onClick={() => setReplyingTo(
                              replyingTo === comment.comment_id ? null : comment.comment_id
                            )}
                          >
                            ↩ Reply
                          </button>
                        )}
                        {['creator', 'final_approver', 'manager'].includes(role) && (
                          <button
                            style={smallBtn('#10b981')}
                            onClick={() => handleResolve(comment.comment_id)}
                          >
                            ✓ Resolve
                          </button>
                        )}
                        {role === 'final_approver' && comment.author_role === 'reviewer' && (
                          <button
                            style={smallBtn('#f59e0b')}
                            onClick={() => handleOverride(comment.comment_id)}
                          >
                            ↺ Override
                          </button>
                        )}
                      </div>
                    )}

                    {/* Reply input */}
                    {replyingTo === comment.comment_id && (
                      <div style={{ marginTop: '8px' }}>
                        <textarea
                          value={replyText}
                          onChange={e => setReplyText(e.target.value)}
                          placeholder="Write a reply..."
                          style={{ ...textareaStyle, minHeight: '56px' }}
                          rows={2}
                          autoFocus
                        />
                        <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
                          <button
                            style={actionBtn(replyText.trim() ? '#6366f1' : '#cbd5e1')}
                            disabled={!replyText.trim()}
                            onClick={() => handleReply(comment.comment_id)}
                          >
                            Send
                          </button>
                          <button
                            style={actionBtn('#94a3b8')}
                            onClick={() => { setReplyingTo(null); setReplyText(''); }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* ── CREATOR: add comment + submit at bottom ── */}
          {role === 'creator' && (
            <div style={{
              padding: '12px 16px',
              borderTop: '1px solid #e2e8f0',
              background: '#fff',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}>
              <textarea
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                placeholder={`Add a note on "${currentPageId || 'this page'}"...`}
                style={textareaStyle}
                rows={2}
              />
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  style={{ ...actionBtn(newComment.trim() ? '#6366f1' : '#cbd5e1'), flex: 1 }}
                  disabled={addingComment || !newComment.trim()}
                  onClick={handleAddComment}
                >
                  {addingComment ? '...' : '💬 Note'}
                </button>
                {['draft', 'changes_requested'].includes(review?.status) && (
                  <button
                    style={{ ...actionBtn('#10b981'), flex: 2 }}
                    disabled={actionLoading}
                    onClick={() => handleWorkflowAction('submit')}
                  >
                    {actionLoading ? '...' : review?.status === 'changes_requested' ? '📤 Resubmit' : '📤 Submit for Review'}
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>

    {/* Rendered outside sidebar via portal so it's not clipped */}
      {showTranscriptEditor && createPortal(
        <TranscriptEditor
          templateId={templateId}
          token={token}
          getAccessTokenSilently={getAccessTokenSilently}
          onClose={(result) => {
            setShowTranscriptEditor(false);
            if (result === 'done' || result === 'skipped') fetchAll();
          }}
          onAudioReady={() => {
            setShowTranscriptEditor(false);
            fetchAll();
          }}
        />,
        document.body
      )}
    </>
  );
}

const actionBtn = (bg) => ({
  width: '100%', padding: '8px 12px', borderRadius: '6px',
  border: `1px solid ${bg}50`, background: `${bg}18`, color: bg,
  fontWeight: 600, fontSize: '12px', cursor: 'pointer',
  backdropFilter: 'blur(4px)',
});
const smallBtn = (color) => ({
  background: 'none', border: 'none', fontSize: '11px',
  fontWeight: 600, color, cursor: 'pointer', padding: '2px 0',
});
const textareaStyle = {
  width: '100%', padding: '8px', borderRadius: '6px',
  border: '1.5px solid rgba(0,255,157,0.15)', fontSize: '12px',
  fontFamily: 'inherit', resize: 'vertical', outline: 'none',
  boxSizing: 'border-box',
  background: 'rgba(0,255,157,0.04)', color: '#f8fafc',
};
